import { useEffect, useRef, useState } from "react";

/**
 * Aura's lightweight in-app photo editor — Phase 1 of Aura Studio.
 *
 * Scope (locked tight on purpose):
 *   • Caption: draggable text overlay with size + color + position presets
 *   • Crop:   aspect-ratio presets (Original / 1:1 / 4:5 / 9:16) — the four
 *             ratios that matter for posting to socials.
 *
 * Everything else (filters, stickers, drawing, AR) stays gated behind the
 * standalone Aura Studio app + Snap Camera/Creative Kit integrations.
 * That's a deliberate UX line: in-app stays useful but not bloated.
 *
 * All work happens on a single <canvas>. No server roundtrip — exports a
 * base64 data URL which the caller can hand to /vision/analyze, /x/tweet,
 * etc. exactly like a regular captured photo.
 *
 * Props:
 *   src           — base64 data URL of the source image
 *   onCancel()    — close without saving
 *   onApply(b64)  — apply: returns the edited base64 (no data:URL prefix)
 */

const ASPECTS = [
  { id: "free",  label: "Original", ratio: null  },
  { id: "1:1",   label: "1:1",      ratio: 1     },
  { id: "4:5",   label: "4:5",      ratio: 4 / 5 },
  { id: "9:16",  label: "9:16",     ratio: 9 / 16 },
];

const CAPTION_COLORS = [
  { id: "white",  hex: "#ffffff", shadow: "#000000" },
  { id: "black",  hex: "#0f0f10", shadow: "#ffffff" },
  { id: "purple", hex: "#c084fc", shadow: "#0f0f10" },
  { id: "gold",   hex: "#fbbf24", shadow: "#0f0f10" },
];

const POSITIONS = [
  { id: "top",    label: "Top"    },
  { id: "middle", label: "Middle" },
  { id: "bottom", label: "Bottom" },
];

export function PhotoEditModal({ src, onCancel, onApply }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [imgReady, setImgReady] = useState(false);

  // Edit state
  const [caption, setCaption] = useState("");
  const [captionColor, setCaptionColor] = useState(CAPTION_COLORS[0]);
  const [captionPosition, setCaptionPosition] = useState("bottom");
  const [captionSize, setCaptionSize] = useState(48); // px in source-space
  const [aspect, setAspect] = useState(ASPECTS[0]);
  const [applying, setApplying] = useState(false);

  // Load the source image once
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      setImgReady(true);
    };
    img.src = src.startsWith("data:") ? src : `data:image/png;base64,${src}`;
  }, [src]);

  // Re-render canvas whenever any edit state changes
  useEffect(() => {
    if (!imgReady) return;
    drawPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgReady, caption, captionColor, captionPosition, captionSize, aspect]);

  /**
   * Compute crop rectangle in source-image coordinates given the chosen aspect.
   * Returns { sx, sy, sw, sh }.
   */
  const computeCrop = () => {
    const img = imgRef.current;
    if (!img) return null;
    const sw0 = img.naturalWidth;
    const sh0 = img.naturalHeight;
    if (!aspect.ratio) return { sx: 0, sy: 0, sw: sw0, sh: sh0 };

    const currentRatio = sw0 / sh0;
    let sw, sh;
    if (currentRatio > aspect.ratio) {
      // source is wider than target → crop sides
      sh = sh0;
      sw = Math.round(sh * aspect.ratio);
    } else {
      // source is taller → crop top/bottom
      sw = sw0;
      sh = Math.round(sw / aspect.ratio);
    }
    const sx = Math.round((sw0 - sw) / 2);
    const sy = Math.round((sh0 - sh) / 2);
    return { sx, sy, sw, sh };
  };

  /**
   * Wraps text into multiple lines so long captions don't overflow.
   * Returns array of line strings.
   */
  const wrapLines = (ctx, text, maxWidth) => {
    if (!text) return [];
    const words = text.split(/\s+/);
    const lines = [];
    let line = "";
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (ctx.measureText(test).width > maxWidth && line) {
        lines.push(line);
        line = w;
      } else {
        line = test;
      }
    }
    if (line) lines.push(line);
    return lines;
  };

  /**
   * Render the cropped image + caption onto the canvas at the size of the
   * cropped region. This same routine produces the final exported base64.
   */
  const drawPreview = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const crop = computeCrop();
    if (!crop) return;

    canvas.width = crop.sw;
    canvas.height = crop.sh;
    const ctx = canvas.getContext("2d");

    // 1. Image (cropped)
    ctx.drawImage(img, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, crop.sw, crop.sh);

    // 2. Caption
    if (caption.trim()) {
      const fontSize = Math.max(20, captionSize);
      ctx.font = `700 ${fontSize}px "Helvetica Neue", Arial, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const maxLineWidth = crop.sw * 0.9;
      const lines = wrapLines(ctx, caption, maxLineWidth);
      const lineHeight = Math.round(fontSize * 1.18);
      const blockHeight = lines.length * lineHeight;

      // Y center of the caption block based on chosen position
      let yCenter;
      const pad = Math.max(24, Math.round(fontSize * 0.6));
      if (captionPosition === "top") {
        yCenter = pad + blockHeight / 2;
      } else if (captionPosition === "middle") {
        yCenter = crop.sh / 2;
      } else {
        yCenter = crop.sh - pad - blockHeight / 2;
      }

      // Soft contrast band behind the text — improves legibility on any photo
      const bandHeight = blockHeight + pad;
      const bandY = yCenter - bandHeight / 2;
      const grad = ctx.createLinearGradient(0, bandY, 0, bandY + bandHeight);
      const darkText = captionColor.id === "black" || captionColor.id === "gold";
      grad.addColorStop(0, darkText ? "rgba(255,255,255,0.0)" : "rgba(0,0,0,0.0)");
      grad.addColorStop(0.5, darkText ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.4)");
      grad.addColorStop(1, darkText ? "rgba(255,255,255,0.0)" : "rgba(0,0,0,0.0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, bandY, crop.sw, bandHeight);

      // Text with subtle shadow stroke for guaranteed legibility
      ctx.lineWidth = Math.max(2, Math.round(fontSize * 0.08));
      ctx.strokeStyle = captionColor.shadow;
      ctx.fillStyle = captionColor.hex;
      lines.forEach((line, i) => {
        const y = yCenter - blockHeight / 2 + lineHeight * (i + 0.5);
        ctx.strokeText(line, crop.sw / 2, y);
        ctx.fillText(line, crop.sw / 2, y);
      });
    }
  };

  const handleApply = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setApplying(true);
    try {
      const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
      const b64 = dataUrl.split(",")[1];
      onApply?.(b64);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div
        className="photo-edit-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="photo-edit-modal"
      >
        <div className="journal-header">
          <h2>✏️ Edit Photo</h2>
          <button
            className="journal-close"
            onClick={onCancel}
            data-testid="photo-edit-close"
          >×</button>
        </div>

        <div className="photo-edit-canvas-wrap">
          {imgReady ? (
            <canvas
              ref={canvasRef}
              className="photo-edit-canvas"
              data-testid="photo-edit-canvas"
            />
          ) : (
            <div className="photo-edit-loading">Loading…</div>
          )}
        </div>

        {/* Caption tools */}
        <div className="photo-edit-section">
          <label className="photo-edit-label">Caption</label>
          <input
            type="text"
            className="photo-edit-input"
            placeholder="Add a caption…"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            maxLength={120}
            data-testid="photo-edit-caption-input"
          />
          {caption.trim() && (
            <>
              <div className="photo-edit-row">
                <span className="photo-edit-sub">Position</span>
                <div className="photo-edit-pills">
                  {POSITIONS.map((p) => (
                    <button
                      key={p.id}
                      className={`photo-edit-pill ${captionPosition === p.id ? "active" : ""}`}
                      onClick={() => setCaptionPosition(p.id)}
                      data-testid={`photo-edit-position-${p.id}`}
                    >{p.label}</button>
                  ))}
                </div>
              </div>
              <div className="photo-edit-row">
                <span className="photo-edit-sub">Color</span>
                <div className="photo-edit-swatches">
                  {CAPTION_COLORS.map((c) => (
                    <button
                      key={c.id}
                      className={`photo-edit-swatch ${captionColor.id === c.id ? "active" : ""}`}
                      style={{ background: c.hex }}
                      onClick={() => setCaptionColor(c)}
                      aria-label={c.id}
                      data-testid={`photo-edit-color-${c.id}`}
                    />
                  ))}
                </div>
              </div>
              <div className="photo-edit-row">
                <span className="photo-edit-sub">Size</span>
                <input
                  type="range"
                  min={24}
                  max={120}
                  value={captionSize}
                  onChange={(e) => setCaptionSize(parseInt(e.target.value, 10))}
                  className="photo-edit-slider"
                  data-testid="photo-edit-size-slider"
                />
              </div>
            </>
          )}
        </div>

        {/* Crop tools */}
        <div className="photo-edit-section">
          <label className="photo-edit-label">Crop</label>
          <div className="photo-edit-pills" data-testid="photo-edit-aspect-pills">
            {ASPECTS.map((a) => (
              <button
                key={a.id}
                className={`photo-edit-pill ${aspect.id === a.id ? "active" : ""}`}
                onClick={() => setAspect(a)}
                data-testid={`photo-edit-aspect-${a.id}`}
              >{a.label}</button>
            ))}
          </div>
        </div>

        {/* Soft note about what's NOT here yet — sets expectation */}
        <div className="photo-edit-locked-hint" data-testid="photo-edit-locked-hint">
          🔒 Filters, stickers & AR are coming soon — they'll unlock when you
          connect the Aura Studio standalone app.
        </div>

        <div className="photo-edit-actions">
          <button
            className="photo-edit-cancel"
            onClick={onCancel}
            disabled={applying}
            data-testid="photo-edit-cancel"
          >Cancel</button>
          <button
            className="photo-edit-apply"
            onClick={handleApply}
            disabled={!imgReady || applying}
            data-testid="photo-edit-apply"
          >
            {applying ? "Applying…" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}
